import { t } from '@lingui/core/macro'
import { motion } from 'framer-motion'
import { Clock, Zap } from 'lucide-react'
import moment from 'moment'
import { ExportList } from '@components/buttons/exportList'
import { ImportList } from '@components/buttons/importList'
import { Toolbar } from '@components/layout/toolbar'
import { PoweredList } from '@components/modals/poweredList'
import { Button } from '@components/v2/button'
import { getList } from '@global/api'
import { createObserverHook } from '@global/hooks/queries/createObserverHook'
import { KEY } from '@global/hooks/queries/useListQuery'
import { ListDTOResponse } from '@global/types'

const useListObserver = createObserverHook<ListDTOResponse>(KEY)

const FadeIn = ({ isActive, className, children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: isActive ? 1 : 0 }}
    transition={{ duration: 0.5 }}
    className={className}
  >
    {children}
  </motion.div>
)

function getTimeAgo(date: Date | string, fullDate = false): string {
  const momentDate = moment.utc(date).local()
  const now = moment()
  const diffMonths = now.diff(momentDate, 'months')

  if (diffMonths < 1 && !fullDate) {
    return momentDate.fromNow()
  }

  return momentDate.format('lll')
}

const UpdatedAtTime = ({ updatedAt }) => (
  <div
    className="text-sm text-cv-blue-600 flex items-center gap-2 px-6 text-nowrap"
    data-tooltip={getTimeAgo(updatedAt, true)}
  >
    <div className="w-[18px]">
      <Clock size={18} />
    </div>
    <span className="flex-shrink-0 font-bold">{t`Last saved:`}</span>
    {getTimeAgo(updatedAt)}
  </div>
)

const PoweredBy = ({ name }) => (
  <div className="text-sm text-cv-blue-600 flex items-center gap-2 px-6 overflow-hidden text-ellipsis w-full min-w-[200px]">
    <div className="flex-shrink-0 w-[18px]">
      <Zap size={18} />
    </div>
    <span className="flex-shrink-0 font-bold">{t`Powered by:`}</span>
    <div
      className="overflow-hidden text-ellipsis whitespace-nowrap min-w-0 flex-grow"
      data-tooltip={name}
    >
      {name}
    </div>
  </div>
)

export const EditToolbar = ({ listId }) => {
  const { data: list } = useListObserver({
    queryFn: ({ signal }) =>
      getList<ListDTOResponse>({
        listId,
        signal,
      }),
    id: listId,
    options: {
      refetchOnWindowFocus: true,
    },
  })

  function onClick() {
    //
  }

  function setReadOnlyCallback() {
    //
  }

  const poweredTemplateFieldId =
    list?.powered_template_id && list?.list_columns[0]?.powered_template_field_id
      ? list?.list_columns[0]?.powered_template_field_id
      : null

  return (
    <Toolbar>
      <Toolbar.Search callback={null} onKeyPress={null} onChange={null} />
      <Toolbar.Left>
        <FadeIn isActive={list} className="flex flex-1 items-center gap-2">
          {list ? (
            <>
              <ExportList listId={String(list.id)} />
              <ImportList
                listId={list.id}
                listName={list.name}
                listDescription={list.notes || ''}
                listType={list.type}
                setReadOnlyCallback={setReadOnlyCallback}
              />
              <PoweredList
                listType={list?.type}
                listId={list.id}
                poweredTemplateId={
                  list?.powered_template_id ? String(list?.powered_template_id) : null
                }
                poweredTemplateFieldId={poweredTemplateFieldId}
              />
              <UpdatedAtTime updatedAt={list.updated_at} />
              <PoweredBy name={list.name} />
            </>
          ) : null}
        </FadeIn>
      </Toolbar.Left>
      <Toolbar.Right>
        <FadeIn isActive={list} className="flex items-center gap-2">
          <Button onClick={onClick} variant="primary">
            {t`Save`}
          </Button>
        </FadeIn>
      </Toolbar.Right>
    </Toolbar>
  )
}
